import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { stockQtySchema, stockVariantSchema } from "./inventory.js";
import { paisa } from "./money.js";
/** Stock counts at one location (docs/specs/inventory.md "Stock counts (B-27)", I-07). */
export const STOCK_COUNT_STATUSES = ["counting", "submitted", "approved", "cancelled"];
export const stockCountStatusSchema = z.enum(STOCK_COUNT_STATUSES);
export const STOCK_COUNT_SCOPES = ["full", "partial"];
export const stockCountScopeSchema = z.enum(STOCK_COUNT_SCOPES);
/** Most variants one count may hold; bigger shops count by category or brand. */
export const MAX_COUNT_LINES = 5000;
/** Most lines one record call may change (a scanner sends one at a time; a paste or batch up to this). */
export const MAX_COUNT_RECORD_LINES = 200;
const zeroOrMore = stockQtySchema.refine((value) => BigInt(value) >= 0n, "Cannot be negative");
const note = z.string().trim().max(500);
const version = z.int().min(1);
const stepSchema = z.object({ at: z.iso.datetime().nullable(), by: z.uuid().nullable() });
export const stockCountFilterSchema = z.object({
    categoryIds: z.array(z.uuid()).max(50),
    brandIds: z.array(z.uuid()).max(50),
});
export const stockCountSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    locationId: z.uuid(),
    scope: stockCountScopeSchema,
    filter: stockCountFilterSchema,
    status: stockCountStatusSchema,
    /** Counters do not see the expected quantities. */
    blind: z.boolean(),
    note: z.string(),
    lineCount: z.int(),
    countedCount: z.int(),
    started: stepSchema,
    submitted: stepSchema,
    approved: stepSchema,
    cancelled: stepSchema,
    version: z.int(),
});
export const stockCountLineSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    variant: stockVariantSchema.extend({ barcodes: z.array(z.string()) }),
    /** On hand when the count started (or when the line was added); null when hidden from a blind counter. */
    snapshotQty: stockQtySchema.nullable(),
    /** Null until counted. */
    countedQty: stockQtySchema.nullable(),
    countedAt: z.iso.datetime().nullable(),
    /** Frozen at approval. */
    expectedQty: stockQtySchema.nullable(),
    varianceQty: stockQtySchema.nullable(),
});
export const stockCountDetailSchema = stockCountSchema.extend({
    /** True when the caller may not see snapshots (a blind count and no `inventory.count_approve`). */
    expectedHidden: z.boolean(),
    lines: z.array(stockCountLineSchema),
});
export const stockCountListQuerySchema = z.object({
    status: stockCountStatusSchema.optional(),
    locationId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
export const stockCountListSchema = z.object({
    data: z.array(stockCountSchema),
    nextCursor: z.uuid().nullable(),
});
/** `POST /v1/stock-counts`: start counting; the snapshot is taken now. */
export const startStockCountRequestSchema = z
    .object({
    locationId: z.uuid(),
    scope: stockCountScopeSchema,
    categoryIds: z.array(z.uuid()).max(50).default([]),
    brandIds: z.array(z.uuid()).max(50).default([]),
    blind: z.boolean().default(true),
    note: note.default(""),
})
    .strict()
    .refine((c) => c.scope === "full" || c.categoryIds.length + c.brandIds.length > 0, {
    message: "Choose at least one category or brand",
    path: ["categoryIds"],
})
    .refine((c) => c.scope === "partial" || c.categoryIds.length + c.brandIds.length === 0, {
    message: "A full count has no filter",
    path: ["categoryIds"],
});
/**
 * `POST …/lines`: `add` adds to what was counted (one scan = one unit), `set` replaces it (typed quantity). A
 * variant not in the count is added if it belongs there.
 */
export const recordStockCountRequestSchema = z
    .object({
    lines: z
        .array(z.object({ variantId: z.uuid(), qty: zeroOrMore, mode: z.enum(["add", "set"]) }).strict())
        .min(1)
        .max(MAX_COUNT_RECORD_LINES),
})
    .strict();
export const recordStockCountResponseSchema = z.object({
    /** The changed lines after the call. */
    lines: z.array(stockCountLineSchema),
    lineCount: z.int(),
    countedCount: z.int(),
    version: z.int(),
});
/** submit / reopen / cancel: the version last read. */
export const stockCountActionRequestSchema = z.object({ version }).strict();
/** `POST …/approve`: lines nobody counted are either taken as zero (not on the shelf) or left alone. */
export const approveStockCountRequestSchema = z
    .object({ version, uncounted: z.enum(["zero", "skip"]) })
    .strict();
export const stockCountVarianceLineSchema = z.object({
    variantId: z.uuid(),
    variant: stockVariantSchema,
    snapshotQty: stockQtySchema,
    /** Ledger movements between the snapshot and the moment the line was counted (sales during the count). */
    movementsQty: stockQtySchema,
    /** What should have been on the shelf when it was counted. */
    expectedQty: stockQtySchema,
    countedQty: stockQtySchema.nullable(),
    countedAt: z.iso.datetime().nullable(),
    /** Counted − expected; null for an uncounted line before approval. */
    varianceQty: stockQtySchema.nullable(),
    /** The variance at the average cost (negative = loss). */
    varianceValuePaisa: paisa(),
    avgCostPaisa: paisa(),
    /** Approved counts: entries from before the line was counted that were posted after approval (late POS sync). */
    lateQty: stockQtySchema,
});
export const stockCountVarianceSchema = z.object({
    count: stockCountSchema,
    lines: z.array(stockCountVarianceLineSchema),
    totals: z.object({
        lines: z.int(),
        counted: z.int(),
        withVariance: z.int(),
        gainPaisa: paisa(),
        lossPaisa: paisa(),
    }),
});
/** `inventory.count.approved`: what was posted, per variant. */
export const stockCountApprovedEventSchema = z.object({
    countId: z.uuid(),
    number: z.string(),
    locationId: z.uuid(),
    lines: z.array(z.object({
        variantId: z.uuid(),
        countedQty: stockQtySchema,
        expectedQty: stockQtySchema,
        varianceQty: stockQtySchema,
    })),
});
// Endpoints -------------------------------------------------------------------------------------------------------
const counts = { tag: "inventory" };
const count = { permission: "inventory.count" };
const approver = { permission: "inventory.count_approve" };
const view = { permission: "inventory.view" };
export const listStockCounts = defineEndpoint({
    ...counts,
    name: "listStockCounts",
    method: "GET",
    path: "/v1/stock-counts",
    access: view,
    summary: "Stock counts at the caller's locations, newest first",
    query: stockCountListQuerySchema,
    status: 200,
    response: stockCountListSchema,
});
export const startStockCount = defineEndpoint({
    ...counts,
    name: "startStockCount",
    method: "POST",
    path: "/v1/stock-counts",
    access: count,
    summary: "Start a full or partial count; snapshots the stock now",
    body: startStockCountRequestSchema,
    status: 201,
    response: stockCountDetailSchema,
    errors: ["LOCATION_INACTIVE", "COUNT_OVERLAP", "COUNT_EMPTY", "COUNT_TOO_LARGE"],
    idempotent: true,
});
export const getStockCount = defineEndpoint({
    ...counts,
    name: "getStockCount",
    method: "GET",
    path: "/v1/stock-counts/{id}",
    access: view,
    summary: "One count with its lines",
    params: idParamsSchema,
    status: 200,
    response: stockCountDetailSchema,
});
export const recordStockCount = defineEndpoint({
    ...counts,
    name: "recordStockCount",
    method: "POST",
    path: "/v1/stock-counts/{id}/lines",
    access: count,
    summary: "Record counted quantities (scan adds, typing sets)",
    params: idParamsSchema,
    body: recordStockCountRequestSchema,
    status: 200,
    response: recordStockCountResponseSchema,
    errors: ["INVALID_TRANSITION", "STOCK_NOT_TRACKED", "COUNT_OVERLAP", "COUNT_TOO_LARGE"],
});
const action = (name, verb, summary) => defineEndpoint({
    ...counts,
    name,
    method: "POST",
    path: `/v1/stock-counts/{id}/${verb}`,
    access: count,
    summary,
    params: idParamsSchema,
    body: stockCountActionRequestSchema,
    status: 200,
    response: stockCountDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
});
export const submitStockCount = action("submitStockCount", "submit", "Hand the count in for approval");
export const reopenStockCount = action("reopenStockCount", "reopen", "Back to counting");
export const cancelStockCount = action("cancelStockCount", "cancel", "Drop the count; nothing is posted");
export const getStockCountVariance = defineEndpoint({
    ...counts,
    name: "getStockCountVariance",
    method: "GET",
    path: "/v1/stock-counts/{id}/variance",
    access: approver,
    summary: "Variance report: expected at the moment each line was counted, counted, difference and value",
    params: idParamsSchema,
    status: 200,
    response: stockCountVarianceSchema,
});
export const approveStockCount = defineEndpoint({
    ...counts,
    name: "approveStockCount",
    method: "POST",
    path: "/v1/stock-counts/{id}/approve",
    access: approver,
    summary: "Post the differences as count_correction adjustments",
    params: idParamsSchema,
    body: approveStockCountRequestSchema,
    status: 200,
    response: stockCountDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
    idempotent: true,
});
//# sourceMappingURL=stock-counts.js.map